import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, Database, X } from 'lucide-react';

const SCHEMA_TEMPLATE = JSON.stringify({
  type:'object',
  properties:{
    id:{type:'integer',example:1},
    name:{type:'string',example:'John Doe'},
    email:{type:'string',format:'email'},
    created_at:{type:'string',format:'date-time'}
  },
  required:['id','name']
},null,2);

export default function SchemasPanel({ projectId }) {
  const [schemas, setSchemas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name:'', description:'', schema_json:SCHEMA_TEMPLATE });
  const [jsonError, setJsonError] = useState('');

  useEffect(() => { load(); }, [projectId]);

  async function load() {
    try { const r = await api.get(`/projects/${projectId}/schemas`); setSchemas(r.data); }
    catch {}
  }

  function select(s) {
    setSelected(s);
    setEditing({ ...s, schema_json: typeof s.schema_json==='string' ? s.schema_json : JSON.stringify(s.schema_json,null,2) });
    setJsonError('');
  }

  function validate(val) {
    try { JSON.parse(val); setJsonError(''); return true; }
    catch(e) { setJsonError(e.message); return false; }
  }

  async function save() {
    if (!validate(editing.schema_json)) return;
    try {
      await api.put(`/schemas/${editing.id}`, { name:editing.name, description:editing.description, schema_json:JSON.parse(editing.schema_json) });
      toast.success('Saved!'); load();
    } catch { toast.error('Failed'); }
  }

  async function create(e) {
    e.preventDefault();
    if (!validate(form.schema_json)) return;
    try {
      const r = await api.post(`/projects/${projectId}/schemas`, { name:form.name, description:form.description, schema_json:JSON.parse(form.schema_json) });
      toast.success('Schema created!');
      setShowCreate(false);
      setForm({ name:'', description:'', schema_json:SCHEMA_TEMPLATE });
      load(); select(r.data);
    } catch { toast.error('Failed'); }
  }

  async function del(id, e) {
    e.stopPropagation();
    if (!window.confirm('Delete schema?')) return;
    try { await api.delete(`/schemas/${id}`); toast.success('Deleted'); if (selected?.id===id){setSelected(null);setEditing(null);} load(); }
    catch { toast.error('Failed'); }
  }

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
      {/* List */}
      <div style={{ width:220, borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', background:'var(--bg-secondary)' }}>
        <div style={{ padding:'12px 12px 8px', borderBottom:'1px solid var(--border)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:14, fontWeight:700 }}>Schemas</span>
          <button className="btn-icon" onClick={() => setShowCreate(true)}><Plus size={14}/></button>
        </div>
        <div style={{ flex:1, overflow:'auto', padding:'6px' }}>
          {!schemas.length && <div style={{ padding:'16px 8px', color:'var(--text-3)', fontSize:12, textAlign:'center' }}>No schemas yet</div>}
          {schemas.map(s => (
            <div key={s.id} style={{ display:'flex', alignItems:'flex-start', gap:7, padding:'8px 10px', borderRadius:7, cursor:'pointer', marginBottom:1, background:selected?.id===s.id?'var(--accent-light)':'transparent' }}
              onClick={() => select(s)}>
              <Database size={13} style={{ flexShrink:0, marginTop:2, color:selected?.id===s.id?'var(--accent)':'var(--purple)' }}/>
              <div style={{ flex:1, overflow:'hidden' }}>
                <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</div>
                {s.description && <div style={{ fontSize:11, color:'var(--text-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.description}</div>}
              </div>
              <button className="btn-icon" style={{ padding:2, flexShrink:0, opacity:.6 }} onClick={e=>del(s.id,e)}><Trash2 size={11} style={{ color:'var(--red)' }}/></button>
            </div>
          ))}
        </div>
      </div>

      {/* Editor */}
      {editing ? (
        <div style={{ flex:1, overflow:'auto', padding:20 }}>
          <div style={{ maxWidth:720 }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, gap:12 }}>
              <div>
                <input style={{ fontSize:17, fontWeight:700, background:'transparent', border:'none', padding:0, fontFamily:'var(--font)', display:'block', marginBottom:5, width:'100%' }}
                  value={editing.name} onChange={e=>setEditing(v=>({...v,name:e.target.value}))} />
                <input style={{ fontSize:12, background:'transparent', border:'none', padding:0, color:'var(--text-3)', fontFamily:'var(--font)', width:'100%' }}
                  value={editing.description||''} onChange={e=>setEditing(v=>({...v,description:e.target.value}))} placeholder="Description" />
              </div>
              <button className="btn btn-primary btn-sm" onClick={save}><Save size={13}/> Save</button>
            </div>
            <div style={{ marginBottom:6, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-2)' }}>JSON Schema</div>
              <button className="btn btn-xs btn-secondary" onClick={() => { try { setEditing(v=>({...v,schema_json:JSON.stringify(JSON.parse(v.schema_json),null,2)})); } catch{} }}>Format</button>
            </div>
            {jsonError && <div style={{ fontSize:11, color:'var(--red)', marginBottom:5, fontFamily:'var(--mono)' }}>{jsonError}</div>}
            <textarea className="code-block" style={{ width:'100%', height:400, resize:'vertical', border: jsonError?'1px solid var(--red)':'1px solid var(--border)', borderRadius:8, padding:14, fontSize:12, lineHeight:1.7 }}
              value={editing.schema_json} onChange={e=>{setEditing(v=>({...v,schema_json:e.target.value}));validate(e.target.value);}} />
          </div>
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10, color:'var(--text-3)' }}>
          <Database size={40} style={{ opacity:.2 }}/><div style={{ fontSize:13 }}>Select a schema to view or edit</div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal modal-wide" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">New Schema</div><button className="btn-icon" onClick={()=>setShowCreate(false)}><X size={16}/></button></div>
            <form onSubmit={create}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Schema Name *</label>
                  <input placeholder="e.g. User" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required autoFocus /></div>
                <div className="form-group"><label className="form-label">Description</label>
                  <input placeholder="Optional" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
              </div>
              <div className="form-group">
                <label className="form-label">JSON Schema</label>
                <textarea className="code-block" style={{ height:200, width:'100%', border:'1px solid var(--border)', padding:12, fontSize:12, resize:'vertical', lineHeight:1.6, borderRadius:8 }}
                  value={form.schema_json} onChange={e=>{setForm(f=>({...f,schema_json:e.target.value}));validate(e.target.value);}} />
                {jsonError && <div style={{ fontSize:11, color:'var(--red)', marginTop:4 }}>{jsonError}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Schema</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
